/**
 * CollaboratorsPanel — one creation's collaborator roster: the creator plus
 * N editors. Editors hold the creator's powers except deleting versions/the
 * creation and managing THIS roster, so:
 *  - creator (or superadmin) → invite via the {@link UserPicker} + Remove
 *    buttons; superadmin actions are attribution-wrapped.
 *  - editors (and other members-to-be) → read-only roster with a note.
 * Mutations go through the trusted addEditor/removeEditor callables; the
 * returned roster is applied immediately (the creation subscription refreshes
 * it authoritatively right after).
 */
import { useEffect, useState } from 'react';
import type { Creation, EditorEntry } from '@djgg/creations';
import { useAuth, useServices } from '../services/context.js';
import { useAttributedAction } from '../hooks/useAttributedAction.js';
import { UserPicker, type PickableProfile } from '../components/UserPicker.js';
import { UserAvatar } from '../components/UserAvatar.js';

const shortUid = (uid: string): string => (uid.length > 10 ? `${uid.slice(0, 10)}…` : uid);

export function CollaboratorsPanel({ creation }: { creation: Creation & { id: string } }) {
  const services = useServices();
  const { user } = useAuth();
  const { runAttributed, attributionModal } = useAttributedAction();

  // Local roster mirror: seeded from the creation, updated instantly from the
  // callable's returned roster (the live creation subscription re-syncs it).
  const [editors, setEditors] = useState<EditorEntry[]>(creation.editors ?? []);
  useEffect(() => {
    setEditors(creation.editors ?? []);
  }, [creation]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Roster management is CREATOR-owned (superadmin with attribution). Editors
  // can build/iterate but not grow or shrink the roster.
  const canManageRoster = !!user && (user.uid === creation.creatorUid || user.role === 'superadmin');
  const memberUids = new Set<string>([creation.creatorUid, ...editors.map((e) => e.uid)]);

  const invite = (profile: PickableProfile): void => {
    void runAttributed(creation, async (attributeTo) => {
      setError('');
      setBusy(true);
      try {
        const res = await services.callable.addEditor({
          creationId: creation.id,
          editorUid: profile.id,
          attributeTo,
        });
        setEditors(res.editors);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to add editor.');
      } finally {
        setBusy(false);
      }
    });
  };

  const remove = (editorUid: string): void => {
    void runAttributed(creation, async (attributeTo) => {
      setError('');
      setBusy(true);
      try {
        const res = await services.callable.removeEditor({
          creationId: creation.id,
          editorUid,
          attributeTo,
        });
        setEditors(res.editors);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to remove editor.');
      } finally {
        setBusy(false);
      }
    });
  };

  return (
    <div className="panel" data-testid="collaborators-panel">
      <h2 className="panel__title">Collaborators — {creation.title}</h2>
      <p className="muted" style={{ marginTop: -6 }}>
        Editors can build, iterate and update the game; only the creator can delete versions,
        delete the game, or change this roster.
      </p>

      <div className="table__scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Collaborator</th>
              <th>Role</th>
              <th>Uid</th>
              {canManageRoster && <th></th>}
            </tr>
          </thead>
          <tbody>
            <tr data-testid="collab-row">
              <td>
                <UserAvatar uid={creation.creatorUid} size={20} /> @{creation.creatorHandle}
              </td>
              <td><span className="pill">creator</span></td>
              <td className="muted">{shortUid(creation.creatorUid)}</td>
              {canManageRoster && <td></td>}
            </tr>
            {editors.map((e) => (
              <tr key={e.uid} data-testid="collab-row">
                <td>
                  <UserAvatar uid={e.uid} size={20} /> @{e.handle || e.uid}
                </td>
                <td><span className="pill">editor</span></td>
                <td className="muted">{shortUid(e.uid)}</td>
                {canManageRoster && (
                  <td>
                    <button
                      className="btn btn--sm btn--danger"
                      onClick={() => remove(e.uid)}
                      disabled={busy}
                      data-testid={`remove-editor-${e.uid}`}
                    >
                      Remove
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {error && <div className="error-text" style={{ marginTop: 10 }}>{error}</div>}

      {canManageRoster ? (
        <div className="field" style={{ marginTop: 16, marginBottom: 0 }}>
          <label className="field__label">Invite editor</label>
          <UserPicker exclude={memberUids} onPick={invite} disabled={busy} />
        </div>
      ) : (
        <p className="muted" style={{ marginTop: 12 }} data-testid="collab-readonly-note">
          Only the game’s creator (or a platform admin) can manage collaborators.
        </p>
      )}

      {attributionModal}
    </div>
  );
}
